// Comms channel detection (1.12.212-beta): the browser side of phone_channel_capabilities (migration 281).
import { useCallback, useEffect, useRef, useState } from 'react';
import { lookupIsDue } from './channelCapability';
import { normalizeOutboundPhoneAddress } from './communicationsCompose';

export const CAPABILITY_COLUMNS = 'id, org_id, phone, line_type, carrier_name, line_type_checked_at, line_type_error, line_type_error_at, whatsapp_status, whatsapp_source, whatsapp_error_code, whatsapp_status_at';

export function capabilityPhone(value) {
    const phone = normalizeOutboundPhoneAddress(String(value || ''));
    return /^\+[1-9]\d{6,14}$/.test(phone) ? phone : '';
}

/** The cached row for this number in the signed-in company (RLS keeps it to that company), or null. */
export async function loadPhoneCapability(client, phone) {
    if (!client?.from || !phone) return null;
    try {
        const { data, error } = await client.from('phone_channel_capabilities').select(CAPABILITY_COLUMNS).eq('phone', phone).maybeSingle();
        if (error) return null;
        return data || null;
    } catch {
        return null;
    }
}

/**
 * Ask the server to check the line type. `force` = "Check now". Never throws: a failure only means no new answer.
 * Returns the capability row the server answered with (or null).
 */
export async function checkPhoneLine(client, phone, { force = false } = {}) {
    if (!client?.functions?.invoke || !phone) return { status: 'skipped', capability: null };
    try {
        const { data, error } = await client.functions.invoke('phone-line-lookup', { body: { phone, ...(force ? { force: true } : {}) } });
        if (error || !data) {
            // Review fix round 1: "Check now" needs Communications edit access (the server answers 403).
            const status = error?.context?.status;
            return { status: status === 403 ? 'forbidden' : 'failed', reason: status === 403 ? 'forbidden' : '', capability: null };
        }
        return {
            status: data.status || 'failed',
            reason: data.reason || '',
            capability: data.capability || null,
            retryAfterSeconds: Number.isFinite(data.retryAfterSeconds) ? data.retryAfterSeconds : null,
            limit: Number.isFinite(data.limit) ? data.limit : null,
        };
    } catch {
        return { status: 'failed', capability: null };
    }
}

/** Staff "Try WhatsApp again": clears a "Not on WhatsApp" mark (the database allows nothing else). */
export async function retryWhatsApp(client, capability) {
    if (!client?.from || !capability?.id) return { ok: false, error: 'Nothing to reset.' };
    try {
        const { data, error } = await client.from('phone_channel_capabilities')
            .update({ whatsapp_status: 'unknown' }).eq('id', capability.id).select(CAPABILITY_COLUMNS).maybeSingle();
        if (error) return { ok: false, error: error.message || 'Could not reset WhatsApp for this number.' };
        return { ok: true, capability: data };
    } catch {
        return { ok: false, error: 'Could not reset WhatsApp for this number.' };
    }
}

/** What staff see after "Check now", in plain words. */
export function checkNowNotice(result = {}) {
    if (result.status === 'checked') return 'Checked just now.';
    if (result.status === 'cached') return 'Already checked — nothing new to pay for.';
    if (result.status === 'forbidden') return 'Only staff who can edit Communications can press "Check now".';
    if (result.status === 'skipped') {
        switch (result.reason) {
            case 'cooldown':
                return `This number was checked moments ago. Try again in ${result.retryAfterSeconds || 60} seconds.`;
            case 'in_flight':
                return 'Someone is already checking this number. The answer will show here in a moment.';
            case 'daily_limit_reached':
                return `Your company has used all ${result.limit ?? ''} number checks for today. Checks start again tomorrow; sending is not affected.`.replace('all  number', 'all number');
            default:
                return 'The check was held (Test limit or paid services off). Try again later.';
        }
    }
    return 'The check did not work this time. Sending is not affected.';
}

const autoChecked = new Set();

/**
 * Review fix round 2: may this staff member press "Check now"? The same rule as phone-line-lookup on the server: an Owner /
 * Admin always may; a User needs Communications EDIT (has_module_access('comms', 'edit')). Fails closed (hidden) when the
 * answer can't be had — the server would refuse anyway.
 */
export async function loadCommsEditAccess(client) {
    if (!client || typeof client.rpc !== 'function') return false;
    try {
        const { data: isUser, error } = await client.rpc('bl395a_current_member_is_user');
        if (error || typeof isUser !== 'boolean') return false;
        if (!isUser) return true;
        const { data: allowed, error: accessError } = await client.rpc('has_module_access', {
            p_module: 'comms', p_action: 'edit', p_entity_id: null, p_group_id: null,
        });
        return !accessError && allowed === true;
    } catch {
        return false;
    }
}

/**
 * The capability for one number, loaded once; a number that was never checked (or not for 12 months) is checked
 * automatically the first time it is used here (the server skips it when the company turned automatic checks off).
 */
export function usePhoneCapability(client, rawPhone, { autoCheck = true } = {}) {
    const phone = capabilityPhone(rawPhone);
    const [capability, setCapability] = useState(null);
    const [checking, setChecking] = useState(false);
    const [notice, setNotice] = useState('');
    const [canCheckNow, setCanCheckNow] = useState(false);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    useEffect(() => {
        let active = true;
        setCanCheckNow(false);
        loadCommsEditAccess(client).then((allowed) => { if (active) setCanCheckNow(allowed); });
        return () => { active = false; };
    }, [client]);

    useEffect(() => {
        let active = true;
        setCapability(null);
        setNotice('');
        if (!phone) return undefined;
        (async () => {
            const row = await loadPhoneCapability(client, phone);
            if (!active) return;
            setCapability(row);
            const key = `${phone}`;
            if (autoCheck && lookupIsDue(row) && !autoChecked.has(key)) {
                autoChecked.add(key);
                const result = await checkPhoneLine(client, phone);
                if (active && result.capability) setCapability(result.capability);
            }
        })();
        return () => { active = false; };
    }, [client, phone, autoCheck]);

    const checkNow = useCallback(async () => {
        if (!phone) return;
        setChecking(true);
        setNotice('');
        const result = await checkPhoneLine(client, phone, { force: true });
        if (!alive.current) return;
        setChecking(false);
        if (result.capability) setCapability(result.capability);
        setNotice(checkNowNotice(result));
    }, [client, phone]);

    const tryWhatsAppAgain = useCallback(async () => {
        const result = await retryWhatsApp(client, capability);
        if (!alive.current) return;
        if (result.ok && result.capability) setCapability(result.capability);
        setNotice(result.ok ? 'WhatsApp can be tried again for this number.' : result.error);
    }, [client, capability]);

    return { phone, capability, checking, notice, checkNow, canCheckNow, tryWhatsAppAgain };
}

export function resetAutoCheckedForTests() {
    autoChecked.clear();
}
